import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from '../components/Toast';
import { getTicketDetails, addMessage, reopenTicket } from '../api/support.api';

const ticketStatusColors = {
    open: 'bg-blue-100 text-blue-800',
    reopened: 'bg-cyan-100 text-cyan-800',
    in_progress: 'bg-yellow-100 text-yellow-800',
    resolved: 'bg-green-100 text-green-800',
    closed: 'bg-muted text-foreground',
};

export default function TicketDetail() {
    const { ticketNumber } = useParams();
    const navigate = useNavigate();
    const messagesContainerRef = useRef(null);

    const [ticket, setTicket] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Reply state
    const [replyMessage, setReplyMessage] = useState('');
    const [sending, setSending] = useState(false);

    // Reopen state
    const [reopening, setReopening] = useState(false);

    useEffect(() => {
        if (ticketNumber) {
            document.title = `Ticket #${ticketNumber} | Fone Express`;
        }
        window.scrollTo(0, 0);
    }, [ticketNumber]);

    const scrollMessagesToBottom = useCallback(() => {
        const el = messagesContainerRef.current;
        if (!el) return;
        el.scrollTop = el.scrollHeight;
    }, []);

    const fetchTicket = useCallback(async ({ scrollMessages = false } = {}) => {
        setLoading(true);
        setError(null);
        try {
            const res = await getTicketDetails(ticketNumber);
            const resData = res?.data || res;
            if (resData?.isOk && resData?.data) {
                setTicket(resData.data);
                if (scrollMessages) {
                    requestAnimationFrame(() => scrollMessagesToBottom());
                }
            } else {
                setError('Ticket not found.');
            }
        } catch (err) {
            console.error('Error fetching ticket:', err);
            setError('Unable to load ticket details. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [ticketNumber, scrollMessagesToBottom]);

    useEffect(() => {
        fetchTicket();
    }, [fetchTicket]);

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!replyMessage.trim()) {
            toast.error('Please enter a message');
            return;
        }
        setSending(true);
        try {
            const res = await addMessage(ticketNumber, { message: replyMessage.trim() });
            const resData = res?.data || res;
            if (resData?.isOk) {
                toast.success('Message sent');
                setReplyMessage('');
                await fetchTicket({ scrollMessages: true });
            } else {
                toast.error(resData?.message || 'Failed to send message');
            }
        } catch (err) {
            console.error('Error sending message:', err);
            toast.error('Failed to send message. Please try again.');
        } finally {
            setSending(false);
        }
    };

    const handleReopen = async () => {
        setReopening(true);
        try {
            const res = await reopenTicket(ticketNumber);
            const resData = res?.data || res;
            if (resData?.isOk) {
                toast.success('Ticket reopened successfully');
                await fetchTicket();
            } else {
                toast.error(resData?.message || 'Failed to reopen ticket');
            }
        } catch (err) {
            console.error('Error reopening ticket:', err);
            toast.error(
                err?.response?.data?.message ||
                    'Failed to reopen ticket. Please try again.',
            );
        } finally {
            setReopening(false);
        }
    };

    const formatStatus = (status) => {
        if (status === 'reopened') return 'Re-Open';
        return (status || '')
            .replace(/_/g, ' ')
            .replace(/\b\w/g, (c) => c.toUpperCase());
    };

    const renderStatusTags = (status) => {
        if (status === 'reopened') {
            return (
                <>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${ticketStatusColors.reopened}`}>
                        Re-Open
                    </span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${ticketStatusColors.in_progress}`}>
                        In Progress
                    </span>
                </>
            );
        }
        return (
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${ticketStatusColors[status] || 'bg-muted text-foreground'}`}>
                {formatStatus(status)}
            </span>
        );
    };

    const isTicketClosed = ticket?.status === 'closed';
    const canReopen =
        ticket?.status === 'resolved' ||
        ticket?.status === 'closed';
    const canReply = !isTicketClosed;

    // Loading Skeleton
    if (loading) {
        return (
            <div className="animate-pulse space-y-4">
                <div className="h-5 bg-muted rounded w-32"></div>
                <div className="h-6 bg-muted rounded w-64"></div>
                <div className="bg-card border border-border rounded-lg p-4 space-y-4">
                    <div className="h-16 bg-muted rounded"></div>
                    <div className="h-16 bg-muted rounded"></div>
                    <div className="h-16 bg-muted rounded"></div>
                </div>
            </div>
        );
    }

    // Error State
    if (error) {
        return (
            <div className="text-center py-12">
                <i className="ri-error-warning-line text-4xl text-red-400 mb-4 block"></i>
                <p className="text-muted-foreground mb-4">{error}</p>
                <button
                    onClick={fetchTicket}
                    className="px-6 py-2 bg-primary text-white rounded-lg hover:brightness-110 transition-all whitespace-nowrap"
                >
                    Try Again
                </button>
            </div>
        );
    }

    if (!ticket) return null;

    return (
        <div className="flex flex-col h-full">
            {/* Back Button */}
            <button
                onClick={() => navigate('/account/support')}
                className="flex items-center gap-1 text-muted-foreground hover:text-primary transition-colors mb-4 text-sm"
            >
                <i className="ri-arrow-left-line"></i>
                Back to Tickets
            </button>

            {/* Ticket Header */}
            <div className="bg-card border border-border rounded-lg p-4 mb-4">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                            <p className="text-xs font-mono text-muted-foreground">{ticket.ticketNumber}</p>
                            {renderStatusTags(ticket.status)}
                        </div>
                        <h1 className="text-xl font-bold text-foreground">{ticket.subject}</h1>
                        <p className="text-xs text-muted-foreground mt-1">
                            Created on{' '}
                            {new Date(ticket.createdAt).toLocaleDateString('en-KE', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                            })}
                        </p>
                    </div>
                    <div className="flex gap-2">
                        {canReopen && (
                            <button
                                onClick={handleReopen}
                                disabled={reopening}
                                className="px-3 py-1.5 border border-blue-500 text-blue-500 rounded-lg text-xs font-medium hover:bg-blue-50 transition-colors disabled:opacity-50 whitespace-nowrap"
                            >
                                {reopening ? (
                                    <i className="ri-loader-4-line animate-spin"></i>
                                ) : (
                                    <>
                                        <i className="ri-refresh-line mr-1"></i>
                                        Reopen Ticket
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                </div>

                {/* Related Order */}
                {ticket.orderNumber && (
                    <div className="mt-3 pt-3 border-t border-border">
                        <p className="text-xs text-muted-foreground">
                            Related Order:{' '}
                            <Link
                                to={`/account/orders/${ticket.orderNumber}`}
                                className="text-primary hover:underline font-medium"
                            >
                                #{ticket.orderNumber}
                            </Link>
                        </p>
                    </div>
                )}
            </div>

            {/* Closed Ticket Banner */}
            {isTicketClosed && (
                <div className="bg-muted border border-border rounded-lg p-3 mb-4 flex items-center gap-2">
                    <i className="ri-lock-line text-muted-foreground"></i>
                    <p className="text-sm text-muted-foreground">
                        This ticket is closed.
                        {canReopen && ' You can reopen it if you need further assistance.'}
                    </p>
                </div>
            )}

            {/* Message Thread */}
            <div className="bg-card border border-border rounded-lg flex-1 mb-4 overflow-hidden">
                <div
                    ref={messagesContainerRef}
                    className="p-4 space-y-4 max-h-[500px] overflow-y-auto"
                >
                    {(ticket.messages || []).length === 0 && (
                        <p className="text-sm text-muted-foreground text-center py-4">No messages yet</p>
                    )}
                    {(ticket.messages || []).map((msg, idx) => {
                        const isCustomer = msg.senderType === 'customer' || msg.sender === 'customer';
                        return (
                            <div
                                key={idx}
                                className={`flex ${isCustomer ? 'justify-end' : 'justify-start'}`}
                            >
                                <div
                                    className={`max-w-[80%] rounded-lg p-3 ${isCustomer
                                        ? 'bg-primary text-white'
                                        : 'bg-muted text-foreground'
                                        }`}
                                >
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className={`text-xs font-medium ${isCustomer ? 'text-white/80' : 'text-muted-foreground'
                                            }`}>
                                            {isCustomer ? 'You' : (msg.senderName || 'Support Agent')}
                                        </span>
                                        <span className={`text-[10px] ${isCustomer ? 'text-white/60' : 'text-muted-foreground'
                                            }`}>
                                            {(() => {
                                                const raw = msg.sentAt || msg.createdAt || msg.timestamp;
                                                const d = raw ? new Date(raw) : null;
                                                if (!d || Number.isNaN(d.getTime())) return null;
                                                return d.toLocaleDateString('en-KE', {
                                                    month: 'short',
                                                    day: 'numeric',
                                                    hour: '2-digit',
                                                    minute: '2-digit',
                                                });
                                            })()}
                                        </span>
                                    </div>
                                    <p className="text-sm whitespace-pre-wrap">{msg.message || msg.text}</p>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Reply Form */}
            {canReply && (
                <form onSubmit={handleSendMessage} className="bg-card border border-border rounded-lg p-4">
                    <div className="flex gap-3">
                        <textarea
                            value={replyMessage}
                            onChange={(e) => setReplyMessage(e.target.value)}
                            rows={2}
                            placeholder="Type your reply..."
                            className="flex-1 border border-border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-primary resize-none"
                        ></textarea>
                        <button
                            type="submit"
                            disabled={sending || !replyMessage.trim()}
                            className="px-4 py-2.5 bg-primary text-white rounded-lg font-medium hover:brightness-110 transition-all disabled:opacity-50 self-end whitespace-nowrap"
                        >
                            {sending ? (
                                <i className="ri-loader-4-line animate-spin"></i>
                            ) : (
                                <i className="ri-send-plane-fill"></i>
                            )}
                        </button>
                    </div>
                </form>
            )}
        </div>
    );
}
